import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import PageHeader from "../components/PageHeader";
import "../css/news.css";
import news1 from "../assets/carousel-1.jpg";
import news2 from "../assets/carousel-2.jpg";
import news3 from "../assets/carousel-3.jpg";
import background4 from '../assets/background-4.jpg';
import { getNews } from "../utils/newsStorage";

export default function NewsDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const all = await getNews();
                const found = all.find(n => String(n.id) === String(id));
                if (!found) {
                    setItem(null);
                } else {
                    const fallbackImages = [news1, news2, news3];
                    const index = all.findIndex(n => String(n.id) === String(id));
                    const fallback = fallbackImages[index % fallbackImages.length];
                    setItem({
                        ...found,
                        featuredImage: found.featuredImage || found.image || fallback
                    });
                }
            } catch (err) {
                console.error("Failed to load news detail", err);
                setItem(null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [id]);

    if (loading) {
        return (
            <>
                <Navbar />
                <PageHeader title="News" path="/news" name="News" bgImage={background4} />
                <div className="history-page container-xxl py-5">
                    <div className="container" style={{ textAlign: "center", padding: "60px 0" }}>
                        <i className="fa fa-spinner fa-spin" style={{ fontSize: "2rem", color: "#002147" }}></i>
                    </div>
                </div>
                <Footer />
                <BackToTop />
            </>
        );
    }

    if (!item) {
        return (
            <>
                <Navbar />
                <PageHeader title="News" path="/news" name="News" bgImage={background4} />
                <div className="history-page container-xxl py-5">
                    <div className="container" style={{ maxWidth: "700px" }}>
                        <button
                            onClick={() => navigate(-1)}
                            style={{
                                border: "none",
                                background: "transparent",
                                color: "#0f172a",
                                marginBottom: "20px",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "0.9rem",
                                cursor: "pointer"
                            }}
                        >
                            <i className="fa fa-arrow-left"></i>
                            Back to News
                        </button>
                        <div className="news-article-card">
                            <h1 className="news-article-title">News item not found</h1>
                            <p className="news-article-body">
                                The news article you are looking for is not available. It may have been removed or the link
                                may be incorrect.
                            </p>
                        </div>
                    </div>
                </div>
                <Footer />
                <BackToTop />
            </>
        );
    }

    const bodyText = item.body || item.excerpt || "";
    const paragraphs = bodyText.split(/\n{2,}/).filter(Boolean);
    const galleryImages = Array.isArray(item.images)
        ? item.images.filter(Boolean)
        : [];

    const normalizeImageBlockContent = (content) => {
        if (Array.isArray(content)) {
            return content.map((v) => String(v).trim()).filter(Boolean);
        }
        if (typeof content === 'string') {
            const trimmed = content.trim();
            if (!trimmed) return [];

            // Supports legacy JSON array string format if any old data exists.
            if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                try {
                    const parsed = JSON.parse(trimmed);
                    if (Array.isArray(parsed)) {
                        return parsed.map((v) => String(v).trim()).filter(Boolean);
                    }
                } catch (e) {
                    // Fall through to CSV parsing.
                }
            }

            return trimmed.split(',').map((v) => v.trim()).filter(Boolean);
        }
        return [];
    };

    const getYouTubeEmbedUrl = (rawUrl) => {
        try {
            const parsed = new URL(rawUrl);
            const host = parsed.hostname.replace(/^www\./, '');

            if (host === 'youtu.be') {
                const id = parsed.pathname.replace(/^\//, '').split('/')[0];
                return id ? `https://www.youtube.com/embed/${id}` : null;
            }

            if (host.includes('youtube.com')) {
                const idFromQuery = parsed.searchParams.get('v');
                if (idFromQuery) return `https://www.youtube.com/embed/${idFromQuery}`;

                const parts = parsed.pathname.split('/').filter(Boolean);
                const embedIndex = parts.findIndex((p) => p === 'embed' || p === 'shorts');
                if (embedIndex !== -1 && parts[embedIndex + 1]) {
                    return `https://www.youtube.com/embed/${parts[embedIndex + 1]}`;
                }
            }
        } catch (err) {
            // Invalid URL, fall back to direct video rendering.
        }
        return null;
    };

    return (
        <>
            <Navbar />
            <PageHeader title="News" path="/news" name="News" bgImage={background4} />
            <div className="news-article-page container-xxl py-5">
                <div className="container" style={{ maxWidth: "900px" }}>
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            border: "none",
                            background: "transparent",
                            color: "#0f172a",
                            marginBottom: "20px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "0.9rem",
                            cursor: "pointer"
                        }}
                    >
                        <i className="fa fa-arrow-left"></i>
                        Back to News
                    </button>

                    <article className="news-article-card">
                        <div className="news-article-meta">
                            {item.category && (
                                <span className="news-article-pill">{item.category}</span>
                            )}
                            <span className="news-article-date">
                                <i className="far fa-calendar-alt"></i> {item.date}
                            </span>
                            {item.author && (
                                <span className="news-article-author">
                                    By {item.author}
                                </span>
                            )}
                            {item.authorisedBy && (
                                <span className="news-article-authorised">
                                    Authorised by {item.authorisedBy}
                                </span>
                            )}
                        </div>

                        <h1 className="news-article-title">{item.title}</h1>
                        {item.subheading && <p className="news-article-subheading">{item.subheading}</p>}

                        {item.featuredImage && (
                            <div className="news-article-hero">
                                <img src={item.featuredImage} alt={item.title} />
                            </div>
                        )}

                        <div className="news-article-body">
                            {item.fullContent && item.fullContent.length > 0 ? (
                                item.fullContent
                                    .sort((a, b) => a.order - b.order)
                                    .map((block, index) => {
                                        if (block.type === 'text') {
                                            return (
                                                <div key={index} className="content-block text-block" dangerouslySetInnerHTML={{ __html: block.content }} />
                                            );
                                        } else if (block.type === 'image') {
                                            const blockImages = normalizeImageBlockContent(block.content);

                                            return (
                                                <div key={index} className="content-block image-block">
                                                    {blockImages.length > 1 ? (
                                                        <div className="news-gallery-grid">
                                                            {blockImages.map((img, imgIndex) => (
                                                                <div key={`${img}-${imgIndex}`} className="news-gallery-item">
                                                                    <img src={img} alt={`Content ${index + 1}-${imgIndex + 1}`} />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <img src={block.content} alt={`Content ${index + 1}`} />
                                                    )}
                                                </div>
                                            );
                                        } else if (block.type === 'video') {
                                            const videoUrl = String(block.content || '').trim();
                                            if (!videoUrl) return null;

                                            const embedUrl = getYouTubeEmbedUrl(videoUrl);
                                            if (embedUrl) {
                                                return (
                                                    <div key={index} className="content-block video-block">
                                                        <iframe
                                                            width="100%"
                                                            height="400"
                                                            src={embedUrl}
                                                            title="YouTube video"
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowFullScreen
                                                        ></iframe>
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div key={index} className="content-block video-block">
                                                        <video controls width="100%" height="400" preload="metadata" playsInline>
                                                            <source src={videoUrl} />
                                                            Your browser does not support the video tag.
                                                        </video>
                                                    </div>
                                                );
                                            }
                                        }
                                        return null;
                                    })
                            ) : (
                                // Fallback to old format
                                (() => {
                                    const bodyText = item.body || item.excerpt || "";
                                    const paragraphs = bodyText.split(/\n{2,}/).filter(Boolean);
                                    return paragraphs.length > 0 ? (
                                        paragraphs.map((p, index) => (
                                            <p key={index}>{p}</p>
                                        ))
                                    ) : (
                                        <p>{bodyText}</p>
                                    );
                                })()
                            )}
                        </div>

                        {galleryImages.length > 0 && (
                            <div className="news-gallery-section">
                                <h3 className="news-gallery-title">News Gallery</h3>
                                <div className="news-gallery-grid">
                                    {galleryImages.map((img, index) => (
                                        <div key={`${img}-${index}`} className="news-gallery-item">
                                            <img src={img} alt={`${item.title} gallery ${index + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </article>
                </div>
            </div>
            <Footer />
            <BackToTop />
        </>
    );
}

